import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import bonusService from '../services/bonusService';
import {
  Gift, Target, CheckCircle, Clock, TrendingUp, Users, DollarSign, ArrowRight, Award, Sparkles, Activity
} from 'lucide-react';

export default function BonusPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => { fetchBonuses(); }, []);

  const fetchBonuses = async () => {
    try {
      setLoading(true);
      const res = await bonusService.getUserBonuses();
      if (res.success && res.data) {
        // Enrichir les données avec les informations de la crypto interne
        const enrichedData = res.data.map(bonus => ({
          ...bonus,
          internal_symbol: bonus.internal_symbol || 'ENOC'
        }));
        setBonuses(enrichedData);
      }
    } catch (err) {
      setError(t('bonus.load_error'));
      console.error(err);
    } finally { setLoading(false); }
  };

  const handleClaim = async (id) => {
    try {
      setClaiming(id);
      const res = await bonusService.claimBonus(id);
      if (res.success) await fetchBonuses();
    } catch (err) {
      alert(err.message || t('bonus.claim_error'));
    } finally { setClaiming(null); }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'deposit': return <DollarSign size={22} />;
      case 'referral': return <Users size={22} />;
      case 'trade_count': return <Target size={22} />;
      case 'trade_volume': return <TrendingUp size={22} />;
      default: return <Gift size={22} />;
    }
  };

  const getIconBg = (type) => {
    switch (type) {
      case 'deposit': return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25';
      case 'referral': return 'bg-indigo-500/15 text-indigo-400 border-indigo-500/25';
      case 'trade_count': return 'bg-amber-500/15 text-amber-400 border-amber-500/25';
      case 'trade_volume': return 'bg-violet-500/15 text-violet-400 border-violet-500/25';
      default: return 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25';
    }
  };

  const claimedCount = bonuses.filter(b => b.status === 'claimed').length;
  const readyCount = bonuses.filter(b => b.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center animate-fade-in">
            <div className="relative w-16 h-16 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-t-2 border-indigo-500 animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center"><Award size={24} className="text-indigo-400 animate-pulse" /></div>
            </div>
            <p className="text-slate-500 font-black uppercase tracking-[0.4em] text-[10px]">{t('bonus.loading_rewards')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-20 space-y-10">

        {/* Header */}
        <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-indigo-600/20 via-violet-600/10 to-slate-900 border border-indigo-500/20 p-8 md:p-12 shadow-[0_40px_80px_-16px_rgba(99,102,241,0.15)]">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row justify-between gap-8 items-start md:items-center">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20">
                <Sparkles size={12} className="text-indigo-400 animate-pulse" />
                <span className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.3em]">{t('bonus.reward_center')}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black font-outfit uppercase tracking-tight">{t('bonus.reward_center')}</h1>
              <p className="text-slate-400 font-medium text-lg">{t('bonus.reward_desc')}</p>
            </div>
            <div className="flex gap-4">
              <div className="text-center p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 min-w-[120px]">
                <p className="text-3xl font-black text-emerald-400 font-outfit">{claimedCount}</p>
                <p className="text-[9px] font-black text-emerald-400/70 uppercase tracking-widest mt-1">{t('bonus.obtained')}</p>
              </div>
              <div className="text-center p-5 rounded-2xl bg-amber-500/10 border border-amber-500/20 min-w-[120px] animate-pulse">
                <p className="text-3xl font-black text-amber-400 font-outfit">{readyCount}</p>
                <p className="text-[9px] font-black text-amber-400/70 uppercase tracking-widest mt-1">{t('bonus.to_claim')}</p>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm font-bold">{error}</div>}

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bonuses.length === 0 ? (
            <div className="col-span-full relative overflow-hidden rounded-[32px] bg-gradient-to-br from-slate-900/60 to-slate-950 border border-white/5 p-20 text-center">
              <div className="absolute inset-0 bg-indigo-500/5 blur-[100px]" />
              <Award size={64} className="text-slate-700 mx-auto mb-6" />
              <h3 className="text-2xl font-black text-white font-outfit mb-3">{t('bonus.no_bonus')}</h3>
              <p className="text-slate-400 font-medium">{t('bonus.come_back_later')}</p>
            </div>
          ) : (
            bonuses.map((bonus) => {
              const progress = Math.min(100, (bonus.current_progress / bonus.target_value) * 100);
              const isCompleted = bonus.status === 'completed';
              const isClaimed = bonus.status === 'claimed';
              const isPending = bonus.status === 'pending';

              return (
                <div key={bonus.id} className={`group relative overflow-hidden rounded-[24px] border transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(99,102,241,0.15)] ${
                  isCompleted ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-slate-900 to-slate-950' :
                  isClaimed ? 'border-white/5 bg-slate-900/40 opacity-75' :
                  'border-white/5 bg-slate-900/40'
                }`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  
                  {/* Badge status */}
                  <div className="absolute top-4 right-4 z-10">
                    {isClaimed ? (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-black uppercase tracking-widest">
                        <CheckCircle size={12} /> {t('bonus.obtained')}
                      </div>
                    ) : isCompleted ? (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-black uppercase tracking-widest animate-pulse">
                        <Award size={12} /> {t('bonus.ready')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-[9px] font-black uppercase tracking-widest">
                        <Clock size={12} /> {t('bonus.in_progress')}
                      </div>
                    )}
                  </div>

                  <div className="p-8">
                    {/* Icon */}
                    <div className={`p-4 rounded-2xl border w-fit mb-6 ${getIconBg(bonus.type)} group-hover:scale-110 transition-transform`}>
                      {getIcon(bonus.type)}
                    </div>

                    <h3 className="text-xl font-black text-white font-outfit uppercase tracking-tight mb-2">{bonus.title}</h3>
                    <p className="text-sm text-slate-400 font-medium leading-relaxed mb-6">{bonus.description}</p>

                    {/* Reward */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-950/50 border border-white/5 mb-6">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('bonus.reward_label')}</span>
                      <div className="text-right">
                        {parseFloat(bonus.internal_split_percent || 0) > 0 ? (
                          <>
                            <div className="text-sm font-black text-indigo-400 font-outfit">
                              {(parseFloat(bonus.reward_amount) * (100 - parseFloat(bonus.internal_split_percent)) / 100).toFixed(2)} {bonus.reward_asset}
                            </div>
                            <div className="text-xs font-bold text-emerald-400 font-outfit mt-1">
                              + {(parseFloat(bonus.reward_amount) * (parseFloat(bonus.internal_split_percent) / 100)).toFixed(2)} {bonus.internal_symbol || 'Token'}
                            </div>
                          </>
                        ) : (
                          <span className="text-base font-black text-indigo-400 font-outfit">{bonus.reward_amount} {bonus.reward_asset}</span>
                        )}
                      </div>
                    </div>

                    {/* Progress */}
                    {!isClaimed && (
                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                          <span className="text-slate-500">Progression</span>
                          <span className="text-slate-400">{bonus.current_progress} / {bonus.target_value}</span>
                        </div>
                        <div className="h-2.5 bg-slate-950 rounded-full overflow-hidden border border-white/5">
                          <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-1000 relative overflow-hidden" style={{ width: `${progress}%` }}>
                            <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent)] animate-shimmer" />
                          </div>
                        </div>
                      </div>
                    )}

                    {isPending && (
                      <div className="flex items-center gap-3 mt-4 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                        <Activity size={14} className="text-indigo-400" />
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{bonus.type === 'deposit' ? 'Dépôt' : bonus.type === 'referral' ? 'Parrainage' : bonus.type === 'trade_count' ? 'Trades' : 'Volume'}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="px-8 pb-8">
                    {isClaimed ? (
                      <button disabled className="w-full py-4 rounded-2xl bg-slate-800/50 text-slate-500 text-[10px] font-black uppercase tracking-widest cursor-not-allowed flex items-center justify-center gap-2">
                        <CheckCircle size={14} /> {t('bonus.already_obtained')}
                      </button>
                    ) : isCompleted ? (
                      <button onClick={() => handleClaim(bonus.id)} disabled={claiming === bonus.id}
                        className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-[0_8px_24px_rgba(16,185,129,0.3)]">
                        {claiming === bonus.id ? (
                          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {t('bonus.claiming')}</>
                        ) : (<><Gift size={14} /> {t('bonus.claim_btn')}</>)}
                      </button>
                    ) : (
                      <button onClick={() => navigate('/dashboard')}
                        className="w-full py-4 rounded-2xl bg-slate-800/50 hover:bg-slate-800 border border-white/5 text-white text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2">
                        Continuer <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}